import { Component, DestroyRef, inject, signal } from '@angular/core';
import {InputTextModule} from 'primeng/inputtext';
import {FloatLabel} from 'primeng/floatlabel';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Password} from 'primeng/password';
import {AuthService} from '../../../services/auth.service';
import {MessageService} from 'primeng/api';
import {Message} from 'primeng/message';
import { distinctUntilChanged, finalize, map, Observable } from 'rxjs';
import {CommonModule} from '@angular/common';
import {ButtonModule} from 'primeng/button';
import { Router } from '@angular/router';
import { InputMaskModule } from 'primeng/inputmask';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type LoginMode = 'email' | 'phone';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    InputTextModule,
    FloatLabel,
    ReactiveFormsModule,
    Password,
    Message,
    ButtonModule,
    InputMaskModule,
  ],
  templateUrl: './login.html',
  styleUrl: './login.css'
})
export class Login {

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  private readonly _router: Router = inject(Router);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _messageService: MessageService = inject(MessageService);

  public loading$: Observable<boolean> = this._authService.loading();
  public activeMode = signal<LoginMode>('email');
  public requestingPhoneCode = signal<boolean>(false);
  public validatingPhoneLogin = signal<boolean>(false);
  public phoneCodeSent = signal<boolean>(false);
  public resendCountdown = signal<number>(0);
  public phoneCodeSentTo = signal<string>('');

  public emailForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  public phoneForm: FormGroup = new FormGroup({
    phone: new FormControl('', [Validators.required]),
    code: new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)]),
  });

  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private lastRequestedPhone = '';

  constructor() {
    this.phone.valueChanges
      .pipe(
        map(value => this._normalizePhone(value)),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((phone) => this._handlePhoneChange(phone));
  }

  get email(): FormControl {
    return this.emailForm.get('email') as FormControl;
  }

  get password(): FormControl {
    return this.emailForm.get('password') as FormControl;
  }

  get phone(): FormControl {
    return this.phoneForm.get('phone') as FormControl;
  }

  get code(): FormControl {
    return this.phoneForm.get('code') as FormControl;
  }

  public setMode(mode: LoginMode): void {
    this.activeMode.set(mode);
  }

  public submitEmail(form: FormGroup): void {
    if (!form.valid) {
      form.markAllAsDirty();
      return;
    }

    this._authService.login(form.getRawValue());
  }

  public resendPhoneCode(): void {
    this._requestPhoneCode(false);
  }

  public submitPhone(): void {
    if (!this.phoneCodeSent()) {
      this.phone.markAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Código pendente',
        detail: 'Informe um telefone válido para receber o código por SMS.',
        life: 3000,
      });
      return;
    }

    if (!this.phoneForm.valid) {
      this.phoneForm.markAllAsDirty();
      return;
    }

    this.validatingPhoneLogin.set(true);

    this._authService.validatePhoneLogin(this.phoneForm.getRawValue())
      .pipe(finalize(() => this.validatingPhoneLogin.set(false)))
      .subscribe({
        error: (error) => this._showAuthError('Falha ao validar código', error),
      });
  }

  public recoverPassword(): void {
    this._router.navigate(['auth', 'recover-password']);
  }

  public ngOnDestroy(): void {
    this._stopCountdown();
  }

  public getResendLabel(): string {
    if (this.requestingPhoneCode()) {
      return 'Enviando...';
    }

    if (this.resendCountdown() > 0) {
      return `Reenviar em ${this.resendCountdown()}s`;
    }

    return this.phoneCodeSent() ? 'Reenviar código' : 'Enviar código';
  }

  public canRequestPhoneCode(): boolean {
    return this._isPhoneComplete(this._normalizePhone(this.phone.value));
  }

  private _handlePhoneChange(phone: string): void {
    if (phone !== this.lastRequestedPhone) {
      this.phoneCodeSent.set(false);
      this.phoneCodeSentTo.set('');
      this.code.reset('', { emitEvent: false });
      this.code.markAsPristine();
      this._stopCountdown();
      this.resendCountdown.set(0);
    }

    if (!this._isPhoneComplete(phone) || this.requestingPhoneCode() || phone === this.lastRequestedPhone) {
      return;
    }

    this._requestPhoneCode(true);
  }

  private _requestPhoneCode(isAutomatic: boolean): void {
    const phone = this.phone.value;
    const normalizedPhone = this._normalizePhone(phone);

    if (!this._isPhoneComplete(normalizedPhone)) {
      this.phone.markAsDirty();

      if (!isAutomatic) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Telefone inválido',
          detail: 'Informe um telefone válido para receber o código.',
          life: 3000,
        });
      }
      return;
    }

    this.requestingPhoneCode.set(true);

    this._authService.requestPhoneLoginCode({ phone })
      .pipe(finalize(() => this.requestingPhoneCode.set(false)))
      .subscribe({
        next: () => {
          this.lastRequestedPhone = normalizedPhone;
          this.phoneCodeSent.set(true);
          this.phoneCodeSentTo.set(phone);
          this._startCountdown();

          if (!isAutomatic) {
            this._messageService.add({
              severity: 'success',
              summary: 'Código reenviado',
              detail: 'Enviamos um novo código por SMS para o telefone informado.',
              life: 3000,
            });
          }

          queueMicrotask(() => document.getElementById('phone_code')?.focus());
        },
        error: (error) => this._showAuthError('Falha ao enviar código', error),
      });
  }

  private _startCountdown(): void {
    this._stopCountdown();
    this.resendCountdown.set(60);

    this.countdownTimer = setInterval(() => {
      const nextValue = this.resendCountdown() - 1;

      if (nextValue <= 0) {
        this._stopCountdown();
        this.resendCountdown.set(0);
        return;
      }

      this.resendCountdown.set(nextValue);
    }, 1000);
  }

  private _stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private _normalizePhone(phone: string | null | undefined): string {
    return (phone || '').replace(/\D/g, '');
  }

  private _isPhoneComplete(phone: string): boolean {
    return phone.length === 11 || (phone.length === 13 && phone.startsWith('55'));
  }

  private _showAuthError(summary: string, error: any): void {
    const detail = error?.error?.errors?.phone?.[0]
      || error?.error?.errors?.code?.[0]
      || error?.error?.message
      || 'Não foi possível concluir a autenticação.';

    this._messageService.add({
      severity: 'error',
      summary,
      detail,
      life: 3000,
    });
  }
}
